import React from 'react';
import { ExternalLink, Users, Clock, TrendingUp } from 'lucide-react';

// Image imports
const dansGamingAvatar = '/images/avatars/dans-gaming.jpg';


// Twitch Icon Component
const TwitchIcon = ({ className }: { className?: string }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
  </svg>
);

export function TwitchStream() {
  const handleTwitchTrackerClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleTwitchClick = () => {
    window.open('https://www.twitch.tv/directory/game/Goosebumps%3A%20Dead%20of%20Night', '_blank', 'noopener,noreferrer');
  };

  const handleTopClipsClick = () => {
    window.open('https://www.twitch.tv/directory/category/goosebumps-dead-of-night/clips?range=all', '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="twitch" className="py-20 relative overflow-hidden bg-slate-900">
      {/* Enhanced Cyberpunk Grid Background */}
      <div className="absolute inset-0">
        {/* Base grid pattern */}
        <div className="absolute inset-0 opacity-40">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: `
                linear-gradient(rgba(34, 211, 238, 0.15) 1px, transparent 1px),
                linear-gradient(90deg, rgba(34, 211, 238, 0.15) 1px, transparent 1px),
                linear-gradient(rgba(168, 85, 247, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(168, 85, 247, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px, 60px 60px, 20px 20px, 20px 20px',
              backgroundPosition: '0 0, 0 0, 0 0, 0 0'
            }}
          ></div>
        </div>

        {/* Enhanced neon circuit patterns */}
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-r from-transparent via-purple-300/25 to-transparent transform -skew-y-1 shadow-lg shadow-purple-400/20"></div>
        <div className="absolute bottom-0 right-0 w-full h-32 bg-gradient-to-l from-transparent via-cyan-300/25 to-transparent transform skew-y-1 shadow-lg shadow-cyan-400/20"></div>
        
        {/* Glowing orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-purple-400/20 to-cyan-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-cyan-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-purple-300/15 to-cyan-300/15 rounded-full blur-3xl"></div>

        {/* Enhanced animated light rays */}
        <div className="absolute top-0 left-1/3 w-1 h-full bg-gradient-to-b from-purple-400/40 via-transparent to-purple-400/40 animate-pulse shadow-lg shadow-purple-400/30"></div>
        <div className="absolute top-0 right-1/3 w-1 h-full bg-gradient-to-b from-cyan-400/40 via-transparent to-cyan-400/40 animate-pulse shadow-lg shadow-cyan-400/30" style={{animationDelay: '1s'}}></div>
        
        {/* Enhanced corner accent elements */}
        <div className="absolute top-0 left-0 w-32 h-32">
          <div className="absolute top-0 left-0 w-16 h-1 bg-gradient-to-r from-purple-400/80 to-transparent shadow-md shadow-purple-400/50"></div>
          <div className="absolute top-0 left-0 w-1 h-16 bg-gradient-to-b from-purple-400/80 to-transparent shadow-md shadow-purple-400/50"></div>
          <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-purple-400/60"></div>
        </div>
        <div className="absolute bottom-0 right-0 w-32 h-32">
          <div className="absolute bottom-0 right-0 w-16 h-1 bg-gradient-to-l from-cyan-400/80 to-transparent shadow-md shadow-cyan-400/50"></div>
          <div className="absolute bottom-0 right-0 w-1 h-16 bg-gradient-to-t from-cyan-400/80 to-transparent shadow-md shadow-cyan-400/50"></div>
          <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-cyan-400/60"></div>
        </div>

        {/* Cyberpunk data streams */}
        <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-400/50 to-transparent animate-pulse"></div>
        <div className="absolute bottom-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="relative inline-block mb-8">
            <div className="flex items-center justify-center space-x-3 text-purple-300 mb-4">
              <span className="text-cyan-300 opacity-90 text-lg cyberpunk-bracket-pulse shadow-lg shadow-cyan-400/30">[</span>
              <div className="relative overflow-hidden">
                <span className="text-white tracking-wider uppercase text-xl font-cyberpunk bg-gradient-to-r from-purple-300 to-cyan-300 bg-clip-text text-transparent relative z-10 cyberpunk-glow drop-shadow-lg">
                  Live on Twitch
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-300/30 to-transparent transform translate-x-full cyberpunk-sheen w-4 shadow-xl">
                </div>
              </div>
              <span className="text-cyan-300 opacity-90 text-lg cyberpunk-bracket-pulse shadow-lg shadow-cyan-400/30">]</span>
            </div>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-purple-300/80 to-transparent cyberpunk-underline"></div>
            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-cyan-300/60 to-transparent blur-sm"></div>
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-px bg-gradient-to-r from-transparent via-purple-300/40 to-transparent blur-sm"></div>
          </div>

          {/* Game Title */}
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-cyberpunk text-white mb-2">
              Goosebumps Dead of Night
            </h2>
            <p className="text-gray-400 font-cyberpunk text-sm tracking-wide">
              Horror Survival Adventure • Available Now
            </p>
          </div>
        </div>

        {/* Twitch Logo Section */}
        <div className="text-center mb-12">
          <div className="relative max-w-md mx-auto">
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div 
                  className="bg-gradient-to-r from-purple-600/20 to-cyan-600/20 backdrop-blur-sm rounded-2xl p-8 border border-purple-400/30 group hover:border-purple-400/60 transition-all duration-300 hover:scale-105 transform cursor-pointer"
                  onClick={handleTwitchClick}
                >
                  <TwitchIcon className="h-20 w-20 text-purple-300 hover:text-white transition-colors duration-300 mx-auto" />
                </div>
                {/* Glowing ring around Twitch logo */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-cyan-400/20 rounded-2xl blur-lg animate-pulse"></div>
              </div>
            </div>


          </div>
        </div>

        {/* Stats Section */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Overall Game Stats */}
            <div className="group relative bg-gradient-to-br from-slate-800/40 to-slate-700/20 backdrop-blur-sm rounded-2xl border border-purple-500/20 overflow-hidden hover:border-purple-400/60 transition-all duration-300 p-8">
              
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-purple-400/0 group-hover:border-purple-400/60 transition-all duration-300"></div>
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-cyan-400/0 group-hover:border-cyan-400/60 transition-all duration-300"></div>
              
              <div className="relative z-10">
                <div className="flex items-center space-x-3 mb-6">
                  <TrendingUp className="h-8 w-8 text-purple-400" />
                  <h3 className="text-xl font-cyberpunk text-white">Peak Performance</h3>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Users className="h-5 w-5 text-cyan-400" />
                      <span className="text-gray-300 font-cyberpunk text-sm">Peak Viewers</span>
                    </div>
                    <span className="text-2xl font-cyberpunk text-cyan-300">50.8K</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-purple-400" />
                      <span className="text-gray-300 font-cyberpunk text-sm">Hours Watched</span>
                    </div>
                    <span className="text-2xl font-cyberpunk text-purple-300">25.2K</span>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-700/50">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400 font-cyberpunk">April 2023</span>
                      <button 
                        onClick={() => handleTwitchTrackerClick('https://twitchtracker.com/games/518064')}
                        className="text-cyan-400 hover:text-cyan-300 transition-colors duration-300 flex items-center space-x-1 font-cyberpunk"
                      >
                        <span>TwitchTracker</span>
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cyberpunk accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-400/30 to-transparent group-hover:via-purple-400/60 transition-all duration-300"></div>
            </div>

            {/* Top Streamer Stats */}
            <div className="group relative bg-gradient-to-br from-slate-800/40 to-slate-700/20 backdrop-blur-sm rounded-2xl border border-cyan-500/20 overflow-hidden hover:border-cyan-400/60 transition-all duration-300 p-8">
              
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-cyan-400/0 group-hover:border-cyan-400/60 transition-all duration-300"></div>
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-purple-400/0 group-hover:border-purple-400/60 transition-all duration-300"></div>
              
              <div className="relative z-10">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="relative">
                    <img 
                      src={dansGamingAvatar} 
                      alt="DansGaming" 
                      className="w-12 h-12 rounded-full border-2 border-cyan-400/50"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-purple-400/20 rounded-full blur-sm"></div>
                  </div>
                  <div>
                    <h3 className="text-xl font-cyberpunk text-white">Top Streamer</h3>
                    <p className="text-cyan-400 font-cyberpunk text-sm">DansGaming</p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Users className="h-5 w-5 text-purple-400" />
                      <span className="text-gray-300 font-cyberpunk text-sm">Peak Viewers</span>
                    </div>
                    <span className="text-2xl font-cyberpunk text-purple-300">8.8K</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-cyan-400" />
                      <span className="text-gray-300 font-cyberpunk text-sm">Hours Watched</span>
                    </div>
                    <span className="text-2xl font-cyberpunk text-cyan-300">17.7K</span>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-700/50">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400 font-cyberpunk">October 2024</span>
                      <button 
                        onClick={() => handleTwitchTrackerClick('https://twitchtracker.com/dansgaming/games/518064')}
                        className="text-cyan-400 hover:text-cyan-300 transition-colors duration-300 flex items-center space-x-1 font-cyberpunk"
                      >
                        <span>TwitchTracker</span>
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cyberpunk accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent group-hover:via-cyan-400/60 transition-all duration-300"></div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-cyberpunk text-white mb-4">
              Join the Horror Experience
            </h3>
            <p className="text-gray-300 font-cyberpunk text-sm leading-relaxed mb-8">
              Watch live streams, follow your favorite streamers, and experience the scares together with the Goosebumps community on Twitch.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={handleTopClipsClick}
                className="bg-gradient-to-r from-purple-500/20 to-cyan-600/20 backdrop-blur-sm rounded-full px-8 py-3 border border-purple-400/30 group hover:border-purple-400/60 transition-all duration-300 hover:scale-105 transform"
              >
                <div className="flex items-center space-x-3">
                  <TwitchIcon className="h-5 w-5 text-purple-300 group-hover:text-white transition-colors duration-300" />
                  <span className="text-purple-300 font-cyberpunk text-sm tracking-wider uppercase group-hover:text-white transition-colors duration-300">
                    Top Clips
                  </span>
                </div>
              </button>
              
              <a 
                href="https://www.goosebumpsgame.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-cyan-500/20 to-purple-600/20 backdrop-blur-sm rounded-full px-8 py-3 border border-cyan-400/30 group hover:border-cyan-400/60 transition-all duration-300 hover:scale-105 transform"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-cyan-300 font-cyberpunk text-sm tracking-wider uppercase group-hover:text-white transition-colors duration-300">
                    Play the Game
                  </span>
                  <ExternalLink className="h-4 w-4 text-cyan-300 group-hover:text-white transition-colors duration-300" />
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}